'use client';

import { useCallback, useEffect, useRef } from 'react';

import { useSocketContext } from '@/contexts/SocketContext';

export const useSocket = (
  service: 'crypto' | 'sports' = 'crypto',
  onMessage?: (data: any) => void,
) => {
  const context = useSocketContext();
  const { socket, isConnected } = context[service];

  useEffect(() => {
    if (socket && onMessage) {
      const handleMessage = (event: MessageEvent) => {
        try {
          let data = event.data;
          if (typeof data === 'string') {
            try {
              data = JSON.parse(data);
            } catch (e) {
              // Not JSON (e.g. "PONG"), keep as string
            }
          }
          onMessage?.(data);
        } catch (err) {
          // ignore or handle silently in prod
        }
      };

      socket.addEventListener('message', handleMessage);

      return () => {
        socket.removeEventListener('message', handleMessage);
      };
    }
  }, [socket, onMessage]);

  const socketRef = useRef<WebSocket | null>(null);
  useEffect(() => {
    socketRef.current = socket;
  }, [socket]);

  const send = useCallback(
    (data: any) => {
      const currentSocket = socketRef.current;
      if (currentSocket) {
        if (currentSocket.readyState === WebSocket.OPEN) {
          const payload = typeof data === 'string' ? data : JSON.stringify(data);
          currentSocket.send(payload);
        }
      }
    },
    [service],
  );

  return {
    socket,
    isConnected,
    send,
  };
};
